import { useEffect, useRef, useState } from "react";
import {
  FlyerReviewSocketContractEventNameDto,
  type PresenceSyncPayload,
} from "@swagger/typescript-fetch-goflyer";
import { io, Socket } from "socket.io-client";
import { ReviewPresenceEntry, presenceKey } from "./types";

/** Canonical `/flyer-review` wire names — keep in sync with OpenAPI `FlyerReviewSocketContractEventNameDto.exampleEvent`. */
const Ev = FlyerReviewSocketContractEventNameDto.ExampleEventEnum;

type ReviewSocketWireEvent =
  FlyerReviewSocketContractEventNameDto.ExampleEventEnum;

/** Socket.IO typings expect `string`; OpenAPI enum members are string at runtime. */
const socketEventName = (
  e: FlyerReviewSocketContractEventNameDto.ExampleEventEnum,
): string => e as unknown as string;

export interface ReviewSocketHandlers {
  onCommentChange: () => void;
  onChangeRequestChange: (payload: unknown) => void;
  onBootstrapChange: () => void;
}

export interface UseReviewSocketResult {
  connected: boolean;
  presence: ReviewPresenceEntry[];
}

const COMMENT_EVENTS: readonly ReviewSocketWireEvent[] = [
  Ev.CommentCreated,
  Ev.CommentUpdated,
  Ev.CommentResolved,
];

const CR_EVENTS: readonly ReviewSocketWireEvent[] = [
  Ev.ChangeRequested,
  Ev.ChangeSuperseded,
  Ev.ChangeAccepted,
  Ev.ChangeRejected,
  Ev.ChangeRestored,
];

const BOOTSTRAP_EVENTS: readonly ReviewSocketWireEvent[] = [
  Ev.FlyerApproved,
  Ev.FlyerReopened,
  Ev.PermissionUpdated,
  Ev.AnyoneUpdated,
  Ev.AudienceRevoked,
];

const upsertPresence = (
  list: ReviewPresenceEntry[],
  entry: ReviewPresenceEntry,
): ReviewPresenceEntry[] => {
  const key = presenceKey(entry);
  const existingIdx = list.findIndex((p) => presenceKey(p) === key);
  if (existingIdx === -1) return [...list, entry];
  const next = list.slice();
  next[existingIdx] = { ...next[existingIdx], ...entry };
  return next;
};

const dedupPresence = (list: ReviewPresenceEntry[]): ReviewPresenceEntry[] => {
  const seen = new Map<string, ReviewPresenceEntry>();
  for (const p of list) {
    const k = presenceKey(p);
    const prev = seen.get(k);
    seen.set(k, prev ? { ...prev, ...p } : p);
  }
  return Array.from(seen.values());
};

export const useReviewSocket = (
  sessionId: string | undefined,
  jwt: string | undefined,
  handlers: ReviewSocketHandlers,
): UseReviewSocketResult => {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState<ReviewPresenceEntry[]>([]);

  useEffect(() => {
    if (!sessionId || !jwt) {
      setConnected(false);
      setPresence([]);
      return;
    }
    const host = process.env.REACT_APP_GOFLYER_API_END_POINT || "";
    const socket: Socket = io(`${host}/flyer-review`, {
      auth: { jwt, sessionId },
      transports: ["websocket"],
    });

    const handleConnect = () => setConnected(true);
    const handleDisconnect = () => setConnected(false);
    const handleConnectError = () => setConnected(false);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    const commentHandler = () => handlersRef.current.onCommentChange();
    const crHandler = (payload: unknown) =>
      handlersRef.current.onChangeRequestChange(payload);
    const bootstrapHandler = () => handlersRef.current.onBootstrapChange();

    COMMENT_EVENTS.forEach((e) =>
      socket.on(socketEventName(e), commentHandler),
    );
    CR_EVENTS.forEach((e) => socket.on(socketEventName(e), crHandler));
    BOOTSTRAP_EVENTS.forEach((e) =>
      socket.on(socketEventName(e), bootstrapHandler),
    );

    const applyPresenceOrSyncPayload = (payload: unknown) => {
      const asSync = payload as Partial<PresenceSyncPayload>;
      if (Array.isArray(asSync.active)) {
        setPresence(dedupPresence(asSync.active as ReviewPresenceEntry[]));
        return;
      }
      const legacy = payload as { list?: ReviewPresenceEntry[] };
      if (Array.isArray(legacy.list)) {
        setPresence(dedupPresence(legacy.list));
        return;
      }
      setPresence((prev) =>
        upsertPresence(prev, payload as ReviewPresenceEntry),
      );
    };

    socket.on(socketEventName(Ev.UserJoined), (payload: ReviewPresenceEntry) =>
      setPresence((prev) => upsertPresence(prev, payload)),
    );
    socket.on(socketEventName(Ev.UserLeft), (payload: ReviewPresenceEntry) => {
      const leavingKey = presenceKey(payload);
      setPresence((prev) => prev.filter((p) => presenceKey(p) !== leavingKey));
    });
    socket.on(socketEventName(Ev.PresenceUpdate), applyPresenceOrSyncPayload);
    socket.on(socketEventName(Ev.PresenceSync), applyPresenceOrSyncPayload);

    return () => {
      socket.disconnect();
      setConnected(false);
      setPresence([]);
    };
  }, [sessionId, jwt]);

  return { connected, presence };
};
